import { api } from "./api/client";
import { apiKey } from "./api/hooks";
import { queryClient } from "./api/query-client";
import { onboardingDoneKey } from "./auth";
import type { Me } from "./types";
import { storage } from "./utils";

/**
 * Onboarding done per org (ALP-79): the server keeps it (`me.orgs[].onboarding`, set with `updateOnboarding`), so every
 * browser and teammate sees the same state. Local storage is only the offline fallback: when the save fails we remember it
 * on this device, treat onboarding as done here, and sync it to the server on the next load.
 */
export function isOnboardingDone(me: Me | null | undefined, orgId: string | null): boolean {
  if (!orgId) return false;
  const local = storage.get(onboardingDoneKey(orgId)) === "1";
  const server = me?.orgs.find((o) => o.id === orgId)?.onboarding;
  if (!server) return local; // an API without the field (older backend): device state only
  if (server.state !== "pending") return true;
  if (local) void syncOnboarding(orgId, "finished"); // finished offline earlier: tell the server now
  return local;
}

function patchMe(orgId: string, state: "finished" | "skipped" | "pending", completedAt: string | null) {
  queryClient.setQueryData<Me>(apiKey("me"), (me) => (me ? { ...me, orgs: me.orgs.map((o) => (o.id === orgId ? { ...o, onboarding: { state, completedAt } } : o)) } : me));
}

async function syncOnboarding(orgId: string, state: "finished" | "skipped") {
  try {
    const r = await api("updateOnboarding", { params: { orgId }, body: { state } });
    patchMe(orgId, r.state, r.completedAt);
    storage.set(onboardingDoneKey(orgId), null);
  } catch {
    /* still offline: keep the device flag */
  }
}

/** Finish or skip onboarding for the org: server first, device flag only when the server can't be reached. */
export async function completeOnboarding(orgId: string, state: "finished" | "skipped"): Promise<void> {
  try {
    const r = await api("updateOnboarding", { params: { orgId }, body: { state } });
    patchMe(orgId, r.state, r.completedAt);
    storage.set(onboardingDoneKey(orgId), null);
  } catch {
    storage.set(onboardingDoneKey(orgId), "1");
    patchMe(orgId, state, new Date().toISOString());
  }
}
